import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getErrorMessage } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { PawPrint, Mail, Lock, Loader2, Eye, EyeOff, ChevronLeft } from 'lucide-react';
import Logo from '../components/Logo';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function Login() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });
    const [showPassword, setShowPassword] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const result = await login(formData.email, formData.password);
            toast.success(`Bem-vindo(a), ${result.user?.name || result.name}!`);
            
            // VERIFICATION GATE: always check first, regardless of role
            if (result.verification_required) {
                navigate('/verification', { replace: true });
                return;
            }
            
            // Redirect based on user type
            const user = result.user || result;
            
            if (user.user_type === 'admin') {
                navigate('/admin', { replace: true });
                return;
            }
            
            // Verificar se tem duplo perfil (only via upgrade, not new registration)
            const isDualProfile = user.is_also_tutor || user.is_dual_profile;
            
            if (isDualProfile) {
                // DUPLO PERFIL: Sempre abre Tutor por padrão
                localStorage.setItem('receppet_active_panel', 'tutor');
                navigate('/tutor', { replace: true });
            } else if (['host', 'caregiver', 'driver', 'partner', 'provider'].includes(user.user_type)) {
                navigate('/provider', { replace: true });
            } else {
                // tutor, client, or any other
                navigate('/tutor', { replace: true });
            }
        } catch (error) {
            toast.error(getErrorMessage(error, 'E-mail ou senha incorretos'));
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = () => {
        const redirectUrl = window.location.origin + '/auth/callback';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-screen hero-gradient flex items-center justify-center p-4" data-testid="login-page">
            <div className="w-full max-w-md">
                {/* Back to home */}
                <div className="mb-4">
                    <button
                        onClick={() => navigate('/')}
                        className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors"
                        data-testid="login-back-btn"
                    >
                        <ChevronLeft className="w-5 h-5" />
                        <span className="text-sm font-medium">Voltar ao início</span>
                    </button>
                </div>
            <Card className="w-full shadow-xl border-0">
                <CardHeader className="text-center pb-2">
                    <div className="flex justify-center mb-4">
                        <Logo size="lg" />
                    </div>
                    <CardTitle className="text-2xl font-bold font-['Nunito']">Entrar</CardTitle>
                    <CardDescription>Entre com sua conta para continuar</CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">E-mail</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="seu@email.com"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="pl-10 bg-white"
                                    required
                                    data-testid="login-email"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password">Senha</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <Input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="••••••••"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    className="pl-10 pr-10 bg-white"
                                    required
                                    data-testid="login-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                    data-testid="toggle-password-visibility"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>
                        <Button 
                            type="submit" 
                            className="w-full btn-primary" 
                            disabled={loading}
                            data-testid="login-submit"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Entrando...
                                </>
                            ) : (
                                'Entrar'
                            )}
                        </Button>
                    </form>

                    {/* Divider */}
                    <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t border-slate-200" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-white px-2 text-slate-500">ou continue com</span>
                        </div>
                    </div>

                    {/* Google Login */}
                    <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={handleGoogleLogin}
                        data-testid="google-login-btn"
                    >
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Entrar com Google
                    </Button>

                    <div className="mt-6 text-center text-sm">
                        <span className="text-slate-500">Não tem uma conta? </span>
                        <Link to="/register" className="text-[#4A90B2] font-medium hover:underline" data-testid="register-link">
                            Cadastre-se
                        </Link>
                    </div>

                    {/* Trust message */}
                    <div className="mt-6 p-3 bg-[#F0F9FF] rounded-xl text-center">
                        <p className="text-xs text-[#4A90B2]">
                            Seus dados são protegidos conforme a LGPD
                        </p>
                    </div>
                </CardContent>
            </Card>
            </div>
        </div>
    );
}
